// @ts-nocheck

import _ from 'lodash';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CoinRotating } from '../../assets/img/spritesheets';
import { getAppDOMElement } from '../../DOMElements';
import { usePrevious } from '../../hooks';
import CoinsTravelAnimation from '../../modules/animations/components/CoinsTravelAnimation';
import {
  selectUserBalance,
  selectUserBankrollVariationBalance,
  selectUserRealMoney,
} from '../../selectors/user/userPrimary.selector';
import { type UserBankrollEntityVariationState } from '../../states/user';
import { BalanceChangeOrigins, BalanceChangeTypes } from '../../types/balanceChanges';
import { TabBarNavigationTypes } from '../../types/tabBar';

const useBankrollVariation = (): [number, number, UserBankrollEntityVariationState] => {
  const userRealMoney = useSelector(selectUserRealMoney);
  const userBalance = useSelector(selectUserBalance);
  const currentBankroll = useMemo(() => (userRealMoney ? userBalance : 0), [userBalance, userRealMoney]);
  const variationInfos = useSelector(selectUserBankrollVariationBalance);
  const previousBankroll = usePrevious(currentBankroll);

  const [bankrollVariation, setBankrollVariation] = useState([]);

  useEffect(() => {
    if (previousBankroll !== currentBankroll) {
      setBankrollVariation((prev) => {
        if (prev) {
          const [prevPrev, prevCurr] = prev;

          if (prevPrev !== previousBankroll && prevCurr !== currentBankroll) {
            return [previousBankroll, currentBankroll, variationInfos];
          }
        }

        return prev;
      });
    }
  }, [previousBankroll, currentBankroll, variationInfos]);

  return bankrollVariation;
};

type NavigationRealCoinsTravelAnimationTriggererProps = {
  className?: string;
};

const NavigationRealCoinsTravelAnimationTriggerer = ({
  className = '',
}: NavigationRealCoinsTravelAnimationTriggererProps) => {
  const bankrollVariation = useBankrollVariation();
  const [, , variationInfos] = bankrollVariation;
  const currentAnimationTs = useRef(null);
  const [showBettingCTA, setShowBettingCTA] = useState(false);
  const [showPokerCTA, setShowPokerCTA] = useState(false);
  const [CTABettingFrom, setCTABettingFrom] = useState(null);
  const [CTAPokerFrom, setCTAPokerFrom] = useState(null);
  const [CTATo, setCTATo] = useState();

  const nbCoins = useMemo(() => {
    const [previousBankroll, currentBankroll] = bankrollVariation;
    const prevBankroll = previousBankroll ?? 0;
    const currBankroll = currentBankroll ?? 0;

    const delta = currBankroll - prevBankroll;

    if (delta > 0) {
      const deltaRatio = delta > 0 ? Math.min(delta / (prevBankroll > 0 ? prevBankroll : 1), 1) : 0;

      if (delta < 100 || deltaRatio < 0.01) {
        return 3;
      }
      if (delta < 500 || deltaRatio < 0.05) {
        return 4;
      }
      if (delta < 1000 || deltaRatio < 0.1) {
        return 5;
      }
      if (deltaRatio && deltaRatio < 0.25) {
        return 7;
      }
      if (deltaRatio && deltaRatio < 0.5) {
        return 10;
      }
      if (deltaRatio && deltaRatio <= 1) {
        return 15;
      }
      if (delta > 0) {
        return 3;
      }
    }

    return 0;
  }, [bankrollVariation]);

  useEffect(() => {
    const appNode = getAppDOMElement();

    const observerCallback = _.debounce(
      () => {
        const fromBettingTarget = document.getElementById(`tabbar-item-${TabBarNavigationTypes.MY_BETS}`);
        const fromPokerTarget = document.getElementById(`winabet-poker-button`);
        const toTarget = document.getElementById('bankroll-real-icon');

        const fromBettingBC = fromBettingTarget?.getBoundingClientRect();
        const fromPokerBC = fromPokerTarget?.getBoundingClientRect();
        const toBC = toTarget?.getBoundingClientRect();

        const setNextCoords = (prevCoords, nextBC) => {
          const nextX = nextBC.x + nextBC.width / 2;
          const nextY = nextBC.y + nextBC.height / 2;

          if (prevCoords?.x !== nextX || prevCoords?.y !== nextY) {
            return {
              x: nextX,
              y: nextY,
            };
          }

          return prevCoords;
        };
        if (fromPokerBC) {
          setCTAPokerFrom((prev) => {
            return setNextCoords(prev, fromPokerBC);
          });
        } else {
          setCTAPokerFrom(null);
        }

        if (fromBettingBC) {
          setCTABettingFrom((prev) => {
            return setNextCoords(prev, fromBettingBC);
          });
        } else {
          setCTABettingFrom(null);
        }

        if (toBC) {
          setCTATo((prev) => {
            return setNextCoords(prev, toBC);
          });
        } else {
          setCTATo(null);
        }
      },
      500,
      { maxWait: 1000 },
    );

    const observer = new MutationObserver(observerCallback);

    observer.observe(appNode, {
      subtree: true,
      childList: true,
    });

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const CTABettingSettings = useMemo(
    () => (CTABettingFrom && CTATo ? { from: CTABettingFrom, to: CTATo } : null),
    [CTABettingFrom, CTATo],
  );

  const CTAPokerSettings = useMemo(
    () => (CTAPokerFrom && CTATo ? { from: CTAPokerFrom, to: CTATo } : null),
    [CTAPokerFrom, CTATo],
  );

  const CTASettings = useMemo(() => {
    if (showBettingCTA) {
      return CTABettingSettings;
    }

    if (showPokerCTA) {
      return CTAPokerSettings;
    }

    return null;
  }, [showBettingCTA, CTABettingSettings, showPokerCTA, CTAPokerSettings]);

  useLayoutEffect(() => {
    if (variationInfos) {
      const { lastChangeOrigin: origin, lastChangeType: type, lastChangeTs: animationTs } = variationInfos;

      if (type === BalanceChangeTypes.CREDIT && !currentAnimationTs.current) {
        if (origin === BalanceChangeOrigins.BETTING) {
          currentAnimationTs.current = animationTs;
          setShowBettingCTA(true);
        } else if (origin === BalanceChangeOrigins.TABLE) {
          currentAnimationTs.current = animationTs;
          setShowPokerCTA(true);
        }
      }
    }
  }, [variationInfos]);

  const onCoinTravelAnimationDone = useCallback(() => {
    currentAnimationTs.current = null;
    setShowBettingCTA(false);
    setShowPokerCTA(false);
  }, []);

  return CTASettings && nbCoins ? (
    <CoinsTravelAnimation
      className={className}
      onAnimationDone={onCoinTravelAnimationDone}
      nbCoins={nbCoins}
      arrivalElementId="bankroll-real-icon"
      spritesheetAsset={CoinRotating}
      {...CTASettings}
    />
  ) : null;
};

export default React.memo(NavigationRealCoinsTravelAnimationTriggerer);
